<template>
  <div class="newsflash-ja-page">
    <div class="newsflash-ja-container">
      <section v-if="loading" class="state-panel">
        <div class="loading-spinner"></div>
        <h2>Cargando noticia flash</h2>
        <p>Recuperando la noticia publicada.</p>
      </section>

      <section v-else-if="errorMessage" class="state-panel">
        <span class="material-icons-outlined state-icon">lock</span>
        <h2>No se puede acceder</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="primary-button" @click="loadItem">Reintentar</button>
      </section>

      <article v-else-if="item" class="published-card">
        <div class="published-hero">
          <span class="meta-chip">{{ formatDate(item.publication_date) }}</span>
          <h1>{{ item.title }}</h1>
        </div>

        <section class="content-block">
          <h2>Que ha pasado</h2>
          <p>{{ item.what_happened }}</p>
        </section>

        <section class="content-block">
          <h2>Consecuencias economicas</h2>
          <p>{{ item.economic_consequences }}</p>
        </section>

        <section class="content-block internal-block">
          <h2>Analisis interno JA</h2>
          <div class="internal-grid">
            <div>
              <span>Volatilidad estimada</span>
              <strong>{{ formatVolatility(item.volatility_percent) }}</strong>
            </div>
            <div>
              <span>Ambito</span>
              <strong>{{ item.news_type_label }}</strong>
            </div>
            <div v-if="item.sector">
              <span>Sector</span>
              <strong>{{ item.sector }}</strong>
            </div>
            <div v-if="item.historical_high_ticker">
              <span>Ticker cerca de extremo historico</span>
              <strong>{{ formatHistoricalHighTicker(item.historical_high_ticker) }}</strong>
            </div>
          </div>
          <div v-if="item.comparable_event" class="comparable-event">
            <span>Suceso comparable</span>
            <strong>{{ item.comparable_event.fecha }} · {{ item.comparable_event.activo_o_mercado_afectado }}</strong>
            <p>{{ item.comparable_event.hecho }}</p>
            <p>{{ item.comparable_event.movimiento_observado }}</p>
            <a v-if="item.comparable_event.fuente_url" :href="item.comparable_event.fuente_url" target="_blank" rel="noopener">
              Fuente del suceso
            </a>
          </div>
          <p v-if="item.volatility_reason">{{ item.volatility_reason }}</p>
        </section>

        <section class="content-block">
          <h2>Valores relacionados</h2>
          <div v-if="item.affected_stocks_count === 0" class="empty-text">
            No hay valores relacionados registrados para esta noticia.
          </div>
          <div v-else class="stocks-list">
            <article
              v-for="stock in item.affected_stocks"
              :key="`${item.slug}-${stock.ticker}`"
              class="stock-item"
            >
              <div class="stock-head">
                <strong>{{ stock.ticker }}</strong>
                <span>{{ stock.nombre_empresa }}</span>
              </div>
              <p v-if="stock.a_que_se_dedica">{{ stock.a_que_se_dedica }}</p>
              <p>{{ stock.como_se_ve_afectado }}</p>
            </article>
          </div>
        </section>

        <footer class="published-disclaimer">
          Esta información tiene finalidad exclusivamente informativa y educativa. No constituye asesoramiento financiero, recomendación de inversión ni invitación a comprar, vender o mantener instrumentos financieros. Cualquier decisión de inversión debe basarse en el análisis propio del usuario.
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import newsflashAiApi from '@/features/newsflash/services/newsflashAiApi';

export default {
  name: 'NewsflashJaPublished',
  data() {
    return {
      item: null,
      loading: false,
      errorMessage: '',
    };
  },
  watch: {
    '$route.fullPath'() {
      this.captureAccessKey();
      this.loadItem();
    },
  },
  mounted() {
    document.body.classList.add('newsflash-ja-review-active');
    this.captureAccessKey();
    this.loadItem();
  },
  beforeDestroy() {
    document.body.classList.remove('newsflash-ja-review-active');
  },
  methods: {
    captureAccessKey() {
      const key = this.$route.query.k;
      if (!key) {
        return;
      }

      newsflashAiApi.storeAccessKey(key);
      const query = { ...this.$route.query };
      delete query.k;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    async loadItem() {
      const slug = this.$route.params.slug;
      if (!slug) {
        this.errorMessage = 'El enlace no contiene una noticia valida.';
        return;
      }

      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await newsflashAiApi.getJaPublishedItem(slug);
        this.item = response.data || null;
      }
      catch (error) {
        this.item = null;
        this.errorMessage = this.resolveErrorMessage(error);
      }
      finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value || '-';
      }

      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    },
    formatVolatility(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '-';
      }

      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(number)}%`;
    },
    formatHistoricalHighTicker(ticker) {
      if (!ticker) {
        return '-';
      }

      const extreme = ticker.tipo_extremo_label || 'Extremo historico';
      const days = Number.isFinite(Number(ticker.dias_desde_extremo)) ? `, hace ${ticker.dias_desde_extremo} dias` : '';
      const date = ticker.fecha_extremo_historico ? `, ${ticker.fecha_extremo_historico}` : '';
      return `${ticker.ticker} - ${ticker.nombre_empresa} (${extreme}${days}${date})`;
    },
    resolveErrorMessage(error) {
      const response = error && error.response ? error.response : null;
      if (response && response.data && response.data.message) {
        return response.data.message;
      }

      if (response && response.status === 403) {
        return 'No tienes permisos o la clave temporal ha caducado.';
      }

      if (response && response.status === 404) {
        return 'La noticia no existe o ya no esta disponible.';
      }

      return 'No se pudo completar la operacion.';
    },
  },
  metaInfo() {
    return {
      title: this.item ? `${this.item.title} | Eurekers` : 'Noticia flash publicada | Eurekers',
    };
  },
};
</script>

<style lang="scss" scoped>
.newsflash-ja-page {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text-primary);
}

.newsflash-ja-container {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
}

.state-panel,
.published-card {
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
}

.state-panel {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px;
  text-align: center;

  h2,
  p {
    margin: 0;
  }
}

.published-card {
  display: grid;
  gap: 16px;
  padding: 20px;
}

.published-hero {
  display: grid;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-outline);

  h1 {
    margin: 0;
    color: var(--color-primary);
    font-size: 28px;
    line-height: 1.25;
  }
}

.meta-chip {
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--color-active-surface);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 700;
}

.content-block {
  padding: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  h2 {
    margin: 0 0 10px;
    color: var(--color-primary);
    font-size: 18px;
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.6;
    white-space: pre-line;
  }
}

.internal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;

  div {
    padding: 10px;
    background: var(--color-surface);
    border: 1px solid var(--color-outline);
    border-radius: 8px;
  }

  span {
    display: block;
    margin-bottom: 4px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }
}

.comparable-event {
  display: grid;
  gap: 6px;
  padding: 12px;
  margin-bottom: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  span {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  p {
    margin: 0;
  }

  a {
    color: var(--color-highlight);
    font-weight: 700;
    text-decoration: none;
  }
}

.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--color-highlight);
  color: var(--color-text-on-highlight);
  font-weight: 600;
  cursor: pointer;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.loading-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid var(--color-outline);
  border-top-color: var(--color-highlight);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.state-icon {
  color: var(--color-error);
  font-size: 34px;
}

.empty-text {
  color: var(--color-text-secondary);
}

.stocks-list {
  display: grid;
  gap: 10px;
}

.stock-item {
  display: grid;
  gap: 8px;
  padding: 12px;
  background: var(--color-surface);
  border-radius: 8px;
}

.stock-head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  strong {
    color: var(--color-primary);
  }

  span {
    color: var(--color-text-secondary);
  }
}

.published-disclaimer {
  padding: 14px 16px 2px;
  color: var(--color-text-secondary);
  font-size: 13px;
  line-height: 1.6;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 560px) {
  .newsflash-ja-container {
    padding: 18px 14px;
  }

  .published-card {
    padding: 16px 14px;
  }

  .published-hero h1 {
    font-size: 24px;
  }

  .internal-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
body.newsflash-ja-review-active #launcher {
  display: none;
}
</style>
